import React, { useMemo } from 'react';
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
} from 'react-bootstrap';

const DbqBulkSendModal = ({
  show,
  onHide,
  mode = 'send',
  providerOptions = [],
  providerUserId = '',
  onProviderChange,
  dbqs = [],
  selectedUserDbqIds = [],
  onToggleUserDbq,
  onSelectAllEligible,
  onClearSelection,
  includeComments = false,
  onIncludeCommentsChange,
  onSubmit,
  isSending = false,
}) => {
  const selectedProvider = useMemo(
    () => providerOptions.find((provider) => String(provider.userId) === String(providerUserId || '')) || null,
    [providerOptions, providerUserId],
  );
  const eligibleDbqs = useMemo(
    () => dbqs.filter((userDbq) => userDbq?.isBulkSendEligible),
    [dbqs],
  );
  const selectedDbqs = useMemo(
    () => dbqs.filter((userDbq) => selectedUserDbqIds.includes(userDbq.userDbqId)),
    [dbqs, selectedUserDbqIds],
  );
  const isRequestChangesMode = mode === 'request_changes';
  const submitLabel = isRequestChangesMode
    ? (
      selectedUserDbqIds.length === 1
        ? 'Request Changes For 1 DBQ'
        : `Request Changes For ${selectedUserDbqIds.length} DBQs`
    )
    : (
      selectedUserDbqIds.length === 1
        ? 'Send 1 DBQ'
        : `Send ${selectedUserDbqIds.length} DBQs`
    );

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem' }}>
          {isRequestChangesMode ? 'Request Changes' : 'Send To Provider'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
          {isRequestChangesMode
            ? 'Choose a provider, review the eligible DBQ requests assigned to that provider, and send one change-request email covering the selected DBQs.'
            : 'Choose a provider, review the eligible DBQ requests assigned to that provider, and send one email covering the selected DBQs.'}
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Provider</Form.Label>
          <Form.Select
            value={providerUserId || ''}
            onChange={(event) => onProviderChange?.(event.target.value)}
            disabled={isSending}
          >
            <option value="">Select provider</option>
            {providerOptions.map((provider) => (
              <option key={provider.userId} value={provider.userId}>
                {provider.name}
              </option>
            ))}
          </Form.Select>
          {selectedProvider ? (
            <Form.Text className="text-muted">
              {isRequestChangesMode ? 'Change-request email' : 'Email'} will be sent to {selectedProvider.email || 'the provider on file'}.
            </Form.Text>
          ) : null}
        </Form.Group>

        {providerUserId ? (
          <>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
              <div className="small text-muted">
                {eligibleDbqs.length} eligible request{eligibleDbqs.length !== 1 ? 's' : ''} for this provider
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={onSelectAllEligible}
                  disabled={isSending || eligibleDbqs.length === 0}
                >
                  Select all eligible
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={onClearSelection}
                  disabled={isSending || selectedUserDbqIds.length === 0}
                >
                  Clear
                </Button>
              </div>
            </div>

            {dbqs.length === 0 ? (
              <Alert variant="warning" className="mb-3">
                This provider does not have any DBQ requests assigned on this claim.
              </Alert>
            ) : (
              <div className="border rounded overflow-hidden mb-3">
                {dbqs.map((userDbq) => (
                  <div
                    key={userDbq.userDbqId}
                    className="d-flex align-items-start gap-3 p-3 border-bottom mb-0"
                    style={{
                      cursor: userDbq.isBulkSendEligible ? 'pointer' : 'not-allowed',
                      background: userDbq.isBulkSendEligible ? '#fff' : '#f8f9fb',
                    }}
                  >
                    <Form.Check
                      type="checkbox"
                      className="mt-1"
                      checked={selectedUserDbqIds.includes(userDbq.userDbqId)}
                      disabled={!userDbq.isBulkSendEligible || isSending}
                      onChange={(event) => onToggleUserDbq?.(userDbq.userDbqId, event.target.checked)}
                    />
                    <div className="flex-grow-1 min-width-0">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <div className="fw-semibold">
                          {userDbq.conditionTitle || userDbq.title || 'Untitled DBQ'}
                        </div>
                        <Badge
                          bg={userDbq.statusMeta?.variant === 'gray' ? 'light' : (userDbq.statusMeta?.variant || 'secondary')}
                          text={userDbq.statusMeta?.variant === 'gray' ? 'dark' : undefined}
                        >
                          {userDbq.statusMeta?.label || 'Draft'}
                        </Badge>
                        {!userDbq.isBulkSendEligible ? (
                          <Badge bg="light" text="dark">
                            Not eligible
                          </Badge>
                        ) : null}
                      </div>
                      <div className="text-muted small mt-1">
                        {userDbq.title || 'Untitled DBQ'}
                      </div>
                      {userDbq.bulkSendEligibilityLabel ? (
                        <div className="small mt-2" style={{ color: '#9a3412' }}>
                          {userDbq.bulkSendEligibilityLabel}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="bulk-send-include-comments"
                label={isRequestChangesMode
                  ? 'Include unresolved comments in the change-request email'
                  : 'Include unresolved comments in the email'}
                checked={includeComments}
                onChange={(event) => onIncludeCommentsChange?.(event.target.checked)}
                disabled={isSending || selectedUserDbqIds.length === 0}
              />
            </Form.Group>

            <div className="rounded border p-3" style={{ background: '#f8f9fb' }}>
              <div className="fw-semibold mb-2">Email preview</div>
              {selectedDbqs.length === 0 ? (
                <div className="text-muted small">
                  Select at least one eligible DBQ to preview the email contents.
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {selectedDbqs.map((userDbq) => (
                    <div key={userDbq.userDbqId}>
                      <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                        {userDbq.title || 'Untitled DBQ'}
                      </div>
                      <div className="text-muted small">
                        {userDbq.conditionTitle || 'No claimed conditions'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <Alert variant="info" className="mb-0">
            Select a provider to load the DBQ requests assigned to them on this claim.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSending}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={isSending || !providerUserId || selectedUserDbqIds.length === 0}
        >
          {isSending
            ? (isRequestChangesMode ? 'Requesting...' : 'Sending...')
            : submitLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DbqBulkSendModal;
